// Bridge so the axios response interceptor (module scope) can decrypt *_enc
// identity blobs (name / headline, encrypted under the shared network key)
// using the key held in CryptoContext. Server never returns readable names once
// a member has migrated; the client resolves them here, transparently.
let _decryptor = null; // async (ct, iv) => plaintext | null
let _resolveReady;
let _readyPromise = new Promise((r) => { _resolveReady = r; });
const _cache = new Map(); // ct -> plaintext

export const setIdentityDecryptor = (fn) => {
  _decryptor = fn;
  if (fn) {
    _resolveReady && _resolveReady();
  } else {
    // Reset for the next session (e.g. after logout) so waiters block again.
    _readyPromise = new Promise((r) => { _resolveReady = r; });
  }
};
export const clearIdentityCache = () => { _cache.clear(); _avatarIv.clear(); };

// path -> iv for encrypted avatars, harvested from any response so the Avatar
// component can decrypt by path without every call site passing the iv.
const _avatarIv = new Map();
export const getAvatarIv = (path) => _avatarIv.get(path) || null;

function _recordAvatarIvs(data, depth) {
  if (data == null || typeof data !== "object" || depth > 6) return;
  if (Array.isArray(data)) { for (const it of data) _recordAvatarIvs(it, depth + 1); return; }
  for (const key of Object.keys(data)) {
    const val = data[key];
    if (key.toLowerCase().includes("avatar") && typeof val === "string" && data[key + "_iv"]) {
      _avatarIv.set(val, data[key + "_iv"]);
    } else if (val && typeof val === "object") {
      _recordAvatarIvs(val, depth + 1);
    }
  }
}

async function _dec(ct, iv) {
  if (_cache.has(ct)) return _cache.get(ct);
  try {
    const plain = await _decryptor(ct, iv);
    if (plain != null) _cache.set(ct, plain);
    return plain;
  } catch {
    return null;
  }
}

function _hasEncBlob(data, depth = 0) {
  if (data == null || typeof data !== "object" || depth > 6) return false;
  if (Array.isArray(data)) return data.some((it) => _hasEncBlob(it, depth + 1));
  for (const key of Object.keys(data)) {
    const val = data[key];
    if (key.endsWith("_enc") && val && typeof val === "object" && val.ct && val.iv) return true;
    if (val && typeof val === "object" && _hasEncBlob(val, depth + 1)) return true;
  }
  return false;
}

async function _walk(data, depth) {
  if (data == null || typeof data !== "object" || depth > 6) return;
  if (Array.isArray(data)) {
    await Promise.all(data.map((it) => _walk(it, depth + 1)));
    return;
  }
  const tasks = [];
  for (const key of Object.keys(data)) {
    const val = data[key];
    if (key.endsWith("_enc") && val && typeof val === "object" && val.ct && val.iv) {
      const base = key.slice(0, -4);
      tasks.push(_dec(val.ct, val.iv).then((p) => { if (p != null) data[base] = p; }));
    } else if (val && typeof val === "object") {
      tasks.push(_walk(val, depth + 1));
    }
  }
  await Promise.all(tasks);
}

// Walk a response body; for any key like `name_enc: {ct, iv}`, decrypt and
// assign the plaintext to the sibling key (`name`). If a payload needs the
// decryptor but it isn't registered yet (cold-boot race), wait briefly for it.
export async function decryptIdentities(data) {
  if (data == null || typeof data !== "object") return data;
  _recordAvatarIvs(data, 0);
  if (!_decryptor) {
    if (!_hasEncBlob(data)) return data;
    await Promise.race([_readyPromise, new Promise((r) => setTimeout(r, 4000))]);
    if (!_decryptor) return data; // still no key (e.g. not unlocked), fall back
  }
  await _walk(data, 0);
  return data;
}
